// Due dates on the web (OEM-267): the badge in a card's badge row, the small date field in an item's
// panel, the "Set due date" dialog from the card's … menu, and the field in New item. "Today" is the
// board's (its time zone, from the board payload), so "overdue 2d" reads the same for everyone.

import { canSetDue, type BoardItem, type Viewer } from "@boem/core/shared";
import { DEFAULT_TIME_ZONE, dueBadge, longDay, todayIn } from "@boem/core/due";
import { createContext, useContext, useEffect, useState } from "react";
import { api, ApiError } from "../api.ts";
import { ErrorLine, Modal } from "./Modal.tsx";

/** Today on the board and its time zone (App provides it from the board payload). */
export const BoardDayContext = createContext<{ today: string; timeZone: string }>({ today: todayIn(DEFAULT_TIME_ZONE), timeZone: DEFAULT_TIME_ZONE });

/** Today on the board in `timeZone`, kept current across midnight (checked every minute). */
export function useBoardToday(timeZone: string = DEFAULT_TIME_ZONE): string {
  const [today, setToday] = useState(() => todayIn(timeZone));
  useEffect(() => {
    setToday(todayIn(timeZone));
    const t = setInterval(() => setToday(todayIn(timeZone)), 60_000);
    return () => clearInterval(t);
  }, [timeZone]);
  return today;
}

/** The badge: "overdue 2d" (red), "due today", "due Wed", "due Oct 21"; a closed item's date is quiet. */
export function DueBadge({ item }: { item: Pick<BoardItem, "due" | "status"> }) {
  const { today } = useContext(BoardDayContext);
  const b = dueBadge(item, today);
  if (!b) return null;
  return (
    <span className="role due-badge" data-tone={b.tone} title={b.title}>
      {b.text}
    </span>
  );
}

/** A native date picker in the tokens' colours; "" is no date. */
export function DateInput({ value, onChange, disabled, label, min }: { value: string; onChange: (v: string) => void; disabled?: boolean; label: string; min?: string }) {
  return <input type="date" className="due-input" value={value} min={min} disabled={disabled} aria-label={label} onChange={(e) => onChange(e.target.value)} />;
}

/** In an item's panel: its due date, and (for whoever may set it) a picker with Clear. `onSet` saves it (LogView). */
export function DueField({ item, viewer, onSet }: { item: BoardItem; viewer: Viewer; onSet: (due: string | null) => unknown }) {
  const [busy, setBusy] = useState(false);
  const may = !item.archived && canSetDue(viewer, item);
  if (!may && !item.due) return null;
  const save = async (v: string | null) => {
    setBusy(true);
    try {
      await onSet(v);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="due-field">
      <span className="due-lbl">Due</span>
      {may ? (
        <>
          <DateInput value={item.due ?? ""} disabled={busy} label={`Due date of #${item.num}`} onChange={(v) => void save(v || null)} />
          {item.due && (
            <button type="button" className="chip sm" disabled={busy} onClick={() => void save(null)}>
              Clear
            </button>
          )}
        </>
      ) : (
        <span>{longDay(item.due!)}</span>
      )}
      <DueBadge item={item} />
    </div>
  );
}

/** "Set due date…" from the card's … menu: pick a day, or clear it. */
export function DueModal({ item, onClose, onSaved }: { item: BoardItem; onClose: () => void; onSaved: (due: string | null) => void }) {
  const { today } = useContext(BoardDayContext);
  const [value, setValue] = useState(item.due ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async (v: string | null) => {
    setBusy(true);
    setError(null);
    try {
      await api.setDue(item.num, v);
      onSaved(v);
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't reach the server. Try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal onClose={onClose}>
      <h3>Due date</h3>
      <p>
        When <b>#{item.num}</b> {item.title} should be done. Days are the board&rsquo;s (today is {longDay(today)}); it shows as overdue on the card once the day has passed and it&rsquo;s still open.
      </p>
      <label>Due on</label>
      <DateInput value={value} label="Due on" disabled={busy} onChange={setValue} />
      <ErrorLine error={error} />
      <div className="row">
        {item.due && (
          <button className="btn" disabled={busy} onClick={() => void save(null)}>
            Clear the date
          </button>
        )}
        <button className="btn" disabled={busy} onClick={onClose}>
          Cancel
        </button>
        <button className="btn primary" disabled={busy || !value || value === item.due} onClick={() => void save(value)}>
          Save
        </button>
      </div>
    </Modal>
  );
}
